import { useMemo, useState } from 'react';
import { useApp } from '../store';
import RenameField from './RenameField';
import {
  MEANS_BLURB,
  MEANS_LABEL,
  MEANS_ORDER,
  liveTypes,
  type Means,
} from '../issue-types';

/*
 * ── WHAT SORT OF WORK A CARD IS, NAMED BY THE BOARD — #302 ──────────────────
 *
 * DELIBERATELY THE SAME SCREEN AS AREAS, next to which it sits: a flat list of
 * rows, one editor open at a time, Remove meaning retire, and a retired list
 * underneath. AreaSettings was built the other way first — every control on
 * every row — and Vinnie's verdict on 27 August 2026 was *"this looks
 * terrible"*. There is no reason to learn that again here.
 *
 * ⚠️ THE ONE THING THIS SCREEN HAS THAT AREAS DOES NOT is the meaning, and it
 * is the whole point of the card. An area's colour is decoration; a type's
 * meaning is what every chart reads and what decides whether a card carries the
 * mark. So it is shown on every row rather than hidden behind Change — somebody
 * scanning this list needs to see at a glance that "Incident" counts as fixing,
 * because that is the thing that will be wrong.
 */
export default function TypeSettings() {
  const types = useApp((s) => s.issueTypes);
  const issues = useApp((s) => s.issues);
  const createType = useApp((s) => s.createType);
  const editType = useApp((s) => s.editType);
  const moveType = useApp((s) => s.moveType);
  const retireType = useApp((s) => s.retireType);
  const restoreType = useApp((s) => s.restoreType);

  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [means, setMeans] = useState<Means>('building');
  const [says, setSays] = useState('');

  const live = liveTypes(types);
  const retired = types.filter((t) => t.retiredAt).sort((a, b) => a.position - b.position);

  /* How many cards each carries — the number that makes Remove a decision. */
  const used = useMemo(() => {
    const n: Record<string, number> = {};
    for (const i of issues) n[i.kind] = (n[i.kind] ?? 0) + 1;
    return n;
  }, [issues]);

  const run = async (p: Promise<string>) => setSays(await p);
  const cards = (slug: string) =>
    used[slug] ? `${used[slug]} ${used[slug] === 1 ? 'card' : 'cards'}` : 'none yet';

  const meansPicker = (chosen: Means, pick: (m: Means) => void, what: string) => (
    <span className="means-picks">
      {MEANS_ORDER.map((m) => (
        <button
          key={m}
          className={`means-pick m-${m} ${m === chosen ? 'is-on' : ''}`}
          aria-pressed={m === chosen}
          aria-label={`${what} counts as ${MEANS_LABEL[m]} — ${MEANS_BLURB[m]}`}
          title={MEANS_BLURB[m]}
          onClick={() => pick(m)}
        >
          <span className="means-dot" />
          {MEANS_LABEL[m]}
        </button>
      ))}
    </span>
  );

  return (
    <section className="panel">
      <h2>What sort of work a card is</h2>
      <p className="hint">
        These are yours to name. A team that talks about defects and enhancements should have
        Defect and Enhancement, not our words for them.
      </p>

      <ul className="area-list">
        {live.map((t, at) => (
          <li key={t.id} className={`area-row ${open === t.id ? 'is-open' : ''}`}>
            <div className="area-line">
              <span className={`means-dot m-${t.means}`} />
              <span className="area-name-flat">{t.name}</span>
              {/* On the row, not behind Change — see the note at the top. */}
              <span className={`means-tag m-${t.means}`}>{MEANS_LABEL[t.means]}</span>
              <span className="area-used">{cards(t.slug)}</span>
              <button
                className="btn btn-small"
                aria-expanded={open === t.id}
                onClick={() => setOpen(open === t.id ? null : t.id)}
              >
                {open === t.id ? 'Done' : 'Change'}
              </button>
              <button className="btn btn-small" onClick={() => void run(retireType(t.id))}>
                Remove
              </button>
            </div>

            {open === t.id && (
              <div className="area-editor">
                {/* One write when the name is finished, not one per letter —
                    see RenameField, which says what that cost. */}
                <RenameField
                  value={t.name}
                  onRename={(name) => void run(editType(t.id, { name }))}
                />
                <div className="area-field">
                  Counts as
                  {meansPicker(t.means, (m) => void run(editType(t.id, { means: m })), t.name)}
                </div>
                <div className="area-field">
                  Order
                  <span className="area-moves">
                    <button
                      className="btn btn-small"
                      disabled={at === 0}
                      aria-label={`Move ${t.name} up`}
                      onClick={() => void run(moveType(t.id, -1))}
                    >
                      ↑
                    </button>
                    <button
                      className="btn btn-small"
                      disabled={at === live.length - 1}
                      aria-label={`Move ${t.name} down`}
                      onClick={() => void run(moveType(t.id, 1))}
                    >
                      ↓
                    </button>
                  </span>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {adding ? (
        <div className="area-editor is-adding">
          <label className="area-field">
            Name
            <input
              className="input"
              value={name}
              autoFocus
              placeholder="Defect, Incident, Tech debt…"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <div className="area-field">
            Counts as
            {meansPicker(means, setMeans, 'the new type')}
          </div>
          <div className="area-adds">
            <button
              className="btn btn-small btn-primary"
              disabled={!name.trim()}
              onClick={async () => {
                const why = await createType({ name, means });
                setSays(why);
                if (!why) {
                  setName('');
                  setAdding(false);
                }
              }}
            >
              Add it
            </button>
            <button
              className="btn btn-small"
              onClick={() => { setAdding(false); setName(''); setSays(''); }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button className="btn btn-small" onClick={() => setAdding(true)}>
          Add a type
        </button>
      )}

      {says && <p className="form-error">{says}</p>}

      {/*
        * ── WHY "COUNTS AS" EXISTS, said where somebody is choosing one ───────
        *
        * Without this the field reads as a category nobody needs to think about,
        * and the first person to file "Incident" under Upkeep will take the
        * firefighting number out of their own reports and never know.
        */}
      <p className="hint area-note">
        <b>&ldquo;Counts as&rdquo; is what the reports read, not the name.</b> Anything counting
        as <b>Fixing</b> carries the mark on its card and makes up the darker part of every bar in
        Insights — so &ldquo;Defect&rdquo; and &ldquo;Incident&rdquo; both do, without either of
        them being called a bug. That is what answers &ldquo;are we building, or firefighting?&rdquo;
      </p>

      <p className="hint area-note">
        <b>Removing a type does not clear it off the cards using it</b>, exactly as with an area.
        It stops being offered on new work and stays where it already is, so last month&rsquo;s
        figures still add up.
      </p>

      {/*
        * THE PUBLISHED CONTRACT, on the screen that breaks it — Vinnie's Q4,
        * 31 August 2026. Somebody renaming a type here is the one person who
        * needs to know that other projects write these words into tracker
        * files, and that nothing they have written is about to stop working.
        */}
      <p className="hint area-note">
        Renaming is free — no card changes, and nothing that has already been written breaks. A
        tracker file may always say <code>task</code>, <code>bug</code>, <code>improvement</code>
        {' '}or <code>feature</code>: those four work on every board for ever and land on whichever
        of your types means the same thing. Your own names work as well. Anything else stops the
        push and says so.
      </p>

      {retired.length > 0 && (
        <>
          <h3 className="area-heading">Retired</h3>
          <ul className="area-list">
            {retired.map((t) => (
              <li key={t.id} className="area-row is-retired">
                <div className="area-line">
                  <span className={`means-dot m-${t.means}`} />
                  <span className="area-name-flat">{t.name}</span>
                  <span className={`means-tag m-${t.means}`}>{MEANS_LABEL[t.means]}</span>
                  <span className="area-used">{cards(t.slug)}</span>
                  <button className="btn btn-small" onClick={() => void run(restoreType(t.id))}>
                    Put back
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
